import { SlicePipe } from '@angular/common';
import { Component, Input, OnInit, signal } from '@angular/core';
import * as ClaimTrackerModels from '@claim-tracker/common/models';

import { expandCollapseAnimation } from '../../shared/animations/expand-collapse.animation';
import { expandListAnimation, expandListHeightAnimation } from '../../shared/animations/expand-list.animation';
import { ActivityStatusComponent } from './activity-status/activity-status.component';

/**
 * Component for displaying claim activity.
 *
 * @author Jesse Bloomster e020902
 */
@Component({
	selector: 'claim-tracker-claim-activity',
	templateUrl: './claim-activity.component.html',
	styleUrls: ['./claim-activity.component.css'],
	imports: [ActivityStatusComponent, SlicePipe],
	animations: [expandListAnimation, expandListHeightAnimation, expandCollapseAnimation()]
})
export class ClaimActivityComponent implements OnInit {
	readonly PAGE_SIZE = 5;
	readonly ANIMATION_STAGGER_DELAY = 50;
	readonly ANIMATION_STAGGER_BASE = 150;

	@Input() claim: ClaimTrackerModels.com.aoins.claims.claim.Claim;

	pageIndex = 0;
	totalActivityStatuses = 0;
	totalVisibleActivityStatuses = 0;
	showMoreInd = signal(false);

	/**
	 * Calculates the height animation speed. We take the total number of visible statuses and  modulus by the
	 * page size to get how many statuses are on the next page. Now we multiple the number of statuses to show
	 * by the animation delay, as each status adds one delay duration to the total duration. Finally we add the
	 * base animation time for 1 status, as that will always be present.
	 *
	 * @param totalNumOfStatuses the total number of statuses to display
	 */
	calculateHeightAnimationSpeed = (totalNumOfStatuses = this.PAGE_SIZE) =>
		(totalNumOfStatuses % this.PAGE_SIZE) * this.ANIMATION_STAGGER_DELAY + this.ANIMATION_STAGGER_BASE;

	/**
	 * Calculates the list container height.
	 */
	getListContainerHeight = () => document.getElementById('activity-statuses-container')?.offsetHeight ?? 0;

	/**
	 * Called when the component is initialized.
	 */
	ngOnInit() {
		this.totalActivityStatuses = this.claim.activityStatuses?.length ?? 0;
		this.updateShowMoreInd();
	}

	/**
	 * Show more activity statuses in the UI.
	 */
	showMoreActivity() {
		this.pageIndex++;
		this.updateShowMoreInd();
	}

	/**
	 * Update the show more indicator. We also update the total number of visible statuses.
	 */
	updateShowMoreInd = () => {
		this.totalVisibleActivityStatuses =
			this.maxStatusesShowing() > this.totalActivityStatuses
				? this.totalActivityStatuses
				: this.maxStatusesShowing();
		this.showMoreInd.set(this.maxStatusesShowing() < this.totalActivityStatuses);
	};

	/**
	 * The maximum number of statuses currently showing (page size x page index).
	 */
	maxStatusesShowing = () => (this.pageIndex + 1) * this.PAGE_SIZE;
}
